"use client";

import React, { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import PageHeaderWithStats from "@/components/common/PageHeaderWithStats";
import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { Footer } from "@/components/Footer";

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  tags: string[];
}

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
}

interface LoggedInUser {
  name: string;
  email: string;
  role: string;
}

const ContactUsPage: React.FC = () => {
  const searchParams = useSearchParams();
  const categoryParam = searchParams.get("category");

  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | null>(null);
  const [activeTab, setActiveTab] = useState<"support" | "contact" | "tickets">(
    "support"
  );
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    category: categoryParam || "general",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tickets, setTickets] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  // Fetch user tickets
  const fetchTickets = async () => {
    if (!loggedInUser) return;

    setLoadingTickets(true);
    try {
      const response = await fetch("/api/support/contact", {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
    } finally {
      setLoadingTickets(false);
    }
  };

  // Check if user is logged in on mount
  useEffect(() => {
    const checkUserSession = async () => {
      try {
        const response = await fetch("/api/auth/session", {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.user) {
            setLoggedInUser(data.user);
            // Pre-fill form if user is logged in
            setFormData((prev) => ({
              ...prev,
              name: data.user.name || "",
              email: data.user.email || "",
            }));
          }
        }
      } catch (error) {
        console.log("User not logged in or session check failed");
      }
    };

    checkUserSession();
  }, []);

  // Fetch tickets when tickets tab is active
  useEffect(() => {
    if (activeTab === "tickets" && loggedInUser) {
      fetchTickets();
    }
  }, [activeTab, loggedInUser]);

  const contactCategories = [
    { id: "general", name: "General Inquiry" },
    { id: "request-class", name: "Request a Class" },
    { id: "technical", name: "Technical Support" },
    { id: "billing", name: "Billing & Payments" },
    { id: "instructor", name: "Instructor Application" },
    { id: "partnership", name: "Partnership Opportunity" },
    { id: "feedback", name: "Feedback & Suggestions" },
  ];

  const faqs: FAQItem[] = [
    {
      id: "1",
      category: "account",
      question: "How do I create an account?",
      answer:
        "Go to the Login page and scroll down to find two register buttons below the login form: 'Register as Student' (for taking classes) or 'Register as Instructor' (for teaching classes). Click the appropriate button based on your role. You'll complete a 2-step registration: (1) Enter your name, email, password, and accept Terms of Service. (2) Complete your fitness profile with goals and preferences. After registration, you'll be automatically logged in and redirected to your dashboard.",
      tags: ["registration", "signup", "new account"],
    },
    {
      id: "2",
      category: "account",
      question: "How do I reset my password?",
      answer:
        "Go to the login page and click 'Forgot Password?' below the login form. Enter your registered email address and click 'Send Reset Link'. You'll receive an email with a secure reset link that's valid for 1 hour. Click the link in the email, enter your new password (must be strong with uppercase, lowercase, numbers, and special characters), and confirm it. After successful reset, you'll be redirected to the login page to sign in with your new password.",
      tags: ["password", "reset", "forgot"],
    },
    {
      id: "3",
      category: "classes",
      question: "How do I book a class?",
      answer:
        "Browse classes on the Classes page, select the class you want, and click 'Book Now'. You'll need to be logged in to book a class. Complete the payment process to confirm your booking. You can view your booked classes in your dashboard under the Bookings tab.",
      tags: ["booking", "classes", "schedule"],
    },
    {
      id: "5",
      category: "payments",
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit cards (Visa, MasterCard, American Express), debit cards, and digital wallets through our secure payment processor PayU. All transactions are encrypted and secure.",
      tags: ["payment", "credit card", "billing"],
    },
    {
      id: "6",
      category: "payments",
      question: "How do refunds work?",
      answer:
        "Once a class is booked and payment is confirmed, all sales are final. Please make sure to check the class schedule, instructor details, and requirements before completing your booking. If you have any concerns about a class, contact our support team.",
      tags: ["refund", "billing", "policy"],
    },
    {
      id: "7",
      category: "technical",
      question: "What equipment do I need for classes?",
      answer:
        "Equipment requirements vary by class type. Each class listing shows required equipment. For yoga classes, you'll typically need a mat. For HIIT classes, dumbbells may be required. Check the class details before booking.",
      tags: ["equipment", "requirements", "setup"],
    },
    {
      id: "8",
      category: "technical",
      question: "What internet speed do I need for live classes?",
      answer:
        "We recommend a minimum of 5 Mbps for smooth video streaming. For the best experience with interactive features and HD video, 10+ Mbps is ideal. You can test your connection before joining any class.",
      tags: ["internet", "connection", "streaming"],
    },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.subject ||
      !formData.message
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the support API endpoint
      const response = await fetch("/api/support/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Support ticket created successfully");
        toast.success(
          data.message ||
          "Message sent successfully! We'll get back to you within 1 business day."
        );

        // Reset form
        setFormData({
          name: loggedInUser?.name || "",
          email: loggedInUser?.email || "",
          subject: "",
          category: "general",
          message: "",
        });

        // Refresh tickets if user is logged in
        if (loggedInUser) {
          fetchTickets();
        }

        // Scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(data.error || "Failed to send message. Please try again.");
      }
    } catch (error) {
      console.log("Contact form error:", error);
      toast.error(
        "Failed to send message. Please try again or email us at support@relaxingroutine.com"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[80px]">
      {/* Header and Stats Section */}
      <PageHeaderWithStats
        badge={{ text: "Contact Us", showPulse: true }}
        title={{ main: "We're Here to", highlighted: "Help You" }}
        description="Have questions or need assistance? Reach out to our team, and we'll get back to you as soon as possible."
        stats={[
          {
            icon: BookOpenIcon,
            value: faqs.length,
            label: "FAQs",
          },
          {
            icon: ChatBubbleLeftRightIcon,
            value: "24/7",
            label: "Support",
          },
          {
            icon: ClockIcon,
            value: "1 Day",
            label: "Response",
          },
          {
            icon: EnvelopeIcon,
            value: "100%",
            label: "Answered",
          },
        ]}
        loading={false}
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 zoom-container">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm p-1">
            <div className="flex space-x-1">
              {[
                {
                  id: "support",
                  label: "Get Support",
                  icon: QuestionMarkCircleIcon,
                },
                {
                  id: "contact",
                  label: "Contact Info",
                  icon: EnvelopeIcon,
                },
                {
                  id: "tickets",
                  label: "My Tickets",
                  icon: ChatBubbleLeftRightIcon,
                },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() =>
                    setActiveTab(tab.id as "support" | "contact" | "tickets")
                  }
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg transition-all ${activeTab === tab.id
                    ? "bg-orange-600 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Support Tab */}
        {activeTab === "support" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-12">
            {/* Still Need Help Form */}
            <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Send us a Message
                </h3>

                {/* Logged-in user indicator */}
                {loggedInUser && (
                  <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {loggedInUser.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          Submitting as: {loggedInUser.name}
                        </p>
                        <p className="text-xs text-blue-700">
                          {loggedInUser.email} •{" "}
                          <span className="capitalize">
                            {loggedInUser.role === "STUDENT"
                              ? "Student"
                              : loggedInUser.role}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={!!loggedInUser}
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="Your full name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!!loggedInUser}
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setIsCategoryDropdownOpen(!isCategoryDropdownOpen)
                        }
                        className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2.5 pr-10 text-left text-gray-900 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all cursor-pointer hover:border-gray-400"
                      >
                        {contactCategories.find(
                          (cat) => cat.id === formData.category
                        )?.name || "Select Category"}
                      </button>
                      <ChevronDownIcon
                        className={`w-5 h-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none transition-transform ${isCategoryDropdownOpen ? "rotate-180" : ""}`}
                      />

                      {isCategoryDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setIsCategoryDropdownOpen(false)}
                          />
                          <div className="absolute z-20 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-60 overflow-auto">
                            {contactCategories.map((category) => (
                              <button
                                key={category.id}
                                type="button"
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    category: category.id,
                                  }));
                                  setIsCategoryDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-2.5 hover:bg-orange-50 transition-colors ${formData.category === category.id
                                  ? "bg-orange-50 text-orange-600 font-medium"
                                  : "text-gray-700"
                                  }`}
                              >
                                {category.name}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Brief subject line"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Please provide detailed information about your issue..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    {isSubmitting ? "Submitting..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Frequently Asked Questions
                </h3>
                <div className="space-y-4">
                  {faqs.map((faq) => (
                    <div
                      key={faq.id}
                      className="border border-gray-200 rounded-lg"
                    >
                      <details className="group">
                        <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-900">
                            {faq.question}
                          </span>
                          <span className="ml-2 flex-shrink-0 text-gray-400 group-open:rotate-180 transition-transform">
                            ▼
                          </span>
                        </summary>
                        <div className="px-4 pb-4">
                          <p className="text-gray-600">{faq.answer}</p>
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact Info Tab */}
        {activeTab === "contact" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-12">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="text-center">
                <div className="p-3 bg-blue-100 rounded-lg inline-block mb-4">
                  <EnvelopeIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Email Support
                </h3>
                <p className="text-gray-600 mb-4">
                  Send us an email for any questions or feedback.
                  {/* <br />
                  <span className="text-xs text-gray-400">Legal Name: Aarav Gupta</span> */}
                </p>
                <a
                  href="mailto:support@relaxingroutine.com"
                  className="text-orange-600 hover:text-orange-700 font-medium"
                >
                  support@relaxingroutine.com
                </a>
                <p className="text-sm text-gray-500 mt-2">
                  We'll reply within 48 hours
                </p>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="text-center">
                <div className="p-3 bg-red-100 rounded-lg inline-block mb-4">
                  <PhoneIcon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Call Us Anytime
                </h3>
                <p className="text-gray-600 mb-4">
                  If you need help right away, give us a call
                </p>
                <a
                  href="tel:9873433324"
                  className="text-orange-600 hover:text-orange-700 font-medium"
                >
                  9873433324
                </a>
                <p className="text-sm text-gray-500 mt-2">
                  Available 24/7
                </p>
              </div>
            </div>

            {/* <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm p-6 md:col-span-2">
              <div className="text-center">
                <div className="p-3 bg-green-100 rounded-lg inline-block mb-4">
                  <span className="text-2xl">📍</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Operating Address
                </h3>
                <p className="text-gray-600">
                  C 986, Patel Nagar, Bareilly, Uttar Pradesh, India
                </p>
              </div>
            </div> */}
          </div>
        )}

        {/* My Tickets Tab */}
        {activeTab === "tickets" && (
          <div className="pb-12">
            {loggedInUser ? (
              <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">
                    Your Support Tickets
                  </h3>

                  {loadingTickets ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading your tickets...</p>
                    </div>
                  ) : tickets.length > 0 ? (
                    <div className="space-y-4">
                      {tickets.map((ticket: any) => (
                        <div
                          key={ticket._id}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-medium text-gray-900">
                                  {ticket.subject}
                                </h4>
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ticket.status === "open"
                                    ? "text-red-600 bg-red-50"
                                    : ticket.status === "in_progress"
                                      ? "text-yellow-600 bg-yellow-50"
                                      : "text-green-600 bg-green-50"
                                    }`}
                                >
                                  {ticket.status === "open"
                                    ? "Open"
                                    : ticket.status === "in_progress"
                                      ? "In Progress"
                                      : "Resolved"}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {ticket.category}
                                </span>
                              </div>
                              <p className="text-gray-600 text-sm mb-2">
                                {ticket.message}
                              </p>
                              {ticket.response && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <p className="text-xs font-semibold text-blue-900 mb-1">
                                    Admin Response:
                                  </p>
                                  <p className="text-sm text-blue-800">
                                    {ticket.response}
                                  </p>
                                  {ticket.responseDate && (
                                    <p className="text-xs text-blue-600 mt-1">
                                      Responded on:{" "}
                                      {new Date(
                                        ticket.responseDate
                                      ).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              )}
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>
                                  Created:{" "}
                                  {new Date(
                                    ticket.createdAt
                                  ).toLocaleDateString()}
                                </span>
                                {ticket.updatedAt && (
                                  <span>
                                    Updated:{" "}
                                    {new Date(
                                      ticket.updatedAt
                                    ).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <ChatBubbleLeftRightIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        No Tickets Yet
                      </h3>
                      <p className="text-gray-600 mb-6">
                        You haven't submitted any support tickets. Need help?
                        Submit a ticket from the "Get Support" tab.
                      </p>
                      <button
                        onClick={() => setActiveTab("support")}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                      >
                        <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                        Submit a Ticket
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-12 text-center">
                  <ExclamationTriangleIcon className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Login Required
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Please log in to view your support tickets.
                  </p>
                  <a
                    href="/login"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                  >
                    Login to Continue
                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ContactUsPage;
